import type { VariantValue } from '@libs/algolia';
import type { Document } from '@contentful/rich-text-types';

import type { ResponseError } from '../response-error';

export type ImageType = {
    type: 'image';
    url: string;
};

export type VideoType = {
    type: 'video';
    poster: string;
    sizes: {
        small: string;
        medium: string;
        large: string;
    };
};

export type MediaEntityType = ImageType | VideoType;

export type PersonalizationType = {
    templateId: string | number;
    templateName: string;
    customizationType: string;
    templateDescription: string;
    fontFaceOptions?: string[];
    isCustomizationMandatory?: boolean;
    styleOptions?: { threadColors?: string[]; styleFinishes?: string[] };
    textLocations?: { frontLines?: number[]; backLines?: number[] };
};

export type DeliveryMethod = {
    isEnabled: boolean;
    purchaseQuantities?: {
        min: number;
        max: number;
    };
};

export type DeliveryMethodSTH = Prettify<
    DeliveryMethod & {
        poBoxRestricted?: boolean;
        isInStock?: boolean; // For SEO purposes only, checks web inventory
    }
>;

export type SeoType = {
    title: string;
    description: string;
    canonicalUrl: string;
};

export type PharmacyType = 'rx_med' | 'rx_food';

export type ProductPharmacyData = {
    type: PharmacyType;
    coldStorage: boolean;
    oos: boolean;
    autoshipBypass: boolean;
    quantity: string;
};

export type Promotion = {
    text: string;
    order: number;
    key?: string | null;
    start?: string | null;
    end?: string | null;
    type?: string | null;
    isDefault?: boolean | null;
    discountDisplay?: string | null;
};

export type VariantKey = 'size' | 'flavor' | 'color' | 'quantity' | 'wattage';
export type VariantValues = Partial<Record<VariantKey, string>>;

// What we get from API
export type Product = {
    name: string;
    brand: Nullable<{ name: string; url: string }>;
    description: Nullable<string>;
    price: {
        original: number;
        sale: Nullable<number>;
        saleValidUntil: Nullable<string>;
    };
    media: MediaEntityType[];
    variantValues: Nullable<VariantValues>;
    rating: { average: number; count: number };
    personalization?: PersonalizationType;
    deliveryMethods: {
        STH: DeliveryMethodSTH;
        BOPIS: DeliveryMethod;
        SDD: DeliveryMethod;
    };
    autoship: {
        isEnabled: boolean;
        frequencyOptions?: { display: string; value: string }[];
        discountDisplay?: string;
    };
    pharmacy: Nullable<ProductPharmacyData>;
    promotions: Nullable<Promotion[]>;
    isLivePet: boolean;
    guestTreatPoints: number;
    upc: string;
    sizeChartImgUrl: string;
};

export type ProductApiResponse = {
    data: {
        webStyleId: string;
        defaultSku: string;
        variants: { [sku: string]: Product };
        variantToSkuMap: Nullable<Record<string, string>>;
        uniqueVariantValues: Nullable<Partial<Record<VariantKey, VariantValue[]>>>;
        primaryCategory: { id: string; name: string };
        categories: string[];
        merchandisingHierarchy: Nullable<{
            category?: string;
            class?: string;
            department?: string;
            division?: string;
            vicePresidentId?: number;
            vicePresidentName?: string;
        }>;
    };
    errors: ResponseError[];
};

export const AUTOSHIP_CONST = {
    OPTIONS: [
        {
            label: '2 weeks',
            value: '2_2',
        },
        {
            label: '3 weeks',
            value: '3_2',
        },
        {
            label: '4 weeks',
            value: '4_2',
        },
        {
            label: '6 weeks',
            value: '6_2',
        },
        {
            label: '8 weeks',
            value: '8_2',
        },
        {
            label: '3 months',
            value: '3_3',
        },
        {
            label: '4 months',
            value: '4_3',
        },
        {
            label: '6 months',
            value: '6_3',
        },
        {
            label: '9 months',
            value: '9_3',
        },
    ],
    FREQUENCY_DROPDOWN_LABEL: 'Choose Frequency',
    FREQUENCY_LABEL: 'Frequency',
    QUANTITY_DROPDOWN_LABEL: 'Quantity',
    ADD_BTN_AUTOSHIP: 'Add for autoship',
    DEFAULT_OPTION: '4_2',
    NOT_AVAIL_MSG: 'Not available for this item',
    AVAIL_MSG: 'Save on recurring orders',
    DRAWER_LINK_TEXT: 'Learn more',
    ADD_TO_AUTOSHIP_TEXT: 'Add to autoship',
    ADDED_TO_AUTOSHIP_TEXT: 'Added to autoship',
    ONE_TIME_LABEL: 'One-time',
    ONE_TIME_VALUE: 'one_time',
} as const;
